import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScanBarcode, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = ['founder', 'super_admin', 'salon', 'salon_staff', 'professional',
  'sales_rep', 'agent', 'warehouse_admin', 'admin_manager', 'distributor'];

export const ScanButton = ({ role }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [hit, setHit] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const token = localStorage.getItem('rk_token');
  const H = { Authorization: `Bearer ${token}` };

  useEffect(() => () => stopCam(), []);
  if (!ROLES.includes(role)) return null;

  const stopCam = () => {
    try { (streamRef.current?.getTracks() || []).forEach(t => t.stop()); } catch (e) {}
    streamRef.current = null;
  };
  const find = async (q) => {
    q = (q || '').trim().toLowerCase();
    if (!q) return;
    const r = await fetch(`${API}/api/c23/scan/check?code=${encodeURIComponent(q)}`, { headers: H });
    const d = await r.json();
    if (d.found) { setHit({ code: q, label: d.label, stock: d.stock || [] }); }
    else toast.error("That code doesn't match a saved barcode or SKU.");
  };
  const startCam = async () => {
    if (!('BarcodeDetector' in window)) { toast.error('This browser has no camera scanner — type the code or use a photo.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      const det = new window.BarcodeDetector();
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await det.detect(videoRef.current);
          if (codes.length) { stopCam(); find(codes[0].rawValue); return; }
        } catch (e) {}
        requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { toast.error('Camera not available — type the code instead.'); }
  };
  const photo = async (file) => {
    if (!('BarcodeDetector' in window)) { toast.error('Photo scanning needs a newer browser — type the code.'); return; }
    try {
      const bmp = await createImageBitmap(file);
      const det = new window.BarcodeDetector();
      const codes = await det.detect(bmp);
      if (codes.length) find(codes[0].rawValue);
      else toast.error('No barcode found on the photo — type the code.');
    } catch (e) { toast.error('Could not read that photo.'); }
  };
  const stockOut = async () => {
    const r = await fetch(`${API}/api/c23/scan/stock-out`, { method: 'POST',
      headers: { ...H, 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: hit.code, where: window.location.pathname }) });
    const d = await r.json();
    if (r.ok) toast.success(`${d.label}: 1 out${d.remaining != null ? ` · ${d.remaining} left` : ''} — logged`);
    else toast.error(d.detail || 'Could not stock out');
  };
  const close = () => { stopCam(); setHit(null); setCode(''); setOpen(false); };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="header-scan-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#D8C49A] bg-[#FBF4E4] px-3 py-1.5 text-xs font-semibold text-[#5A4632] hover:bg-[#F3E8CE] transition-colors">
        <ScanBarcode className="w-4 h-4" /> Scan
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[300] bg-black/50 flex items-end sm:items-center justify-center" onClick={close}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="scan-sheet">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#2B1F27]">Scan a product</h3>
              <button onClick={close} data-testid="scan-close"><X className="w-5 h-5" /></button>
            </div>
            <video ref={videoRef} className="w-full rounded-lg bg-black/5 mb-3" style={{ maxHeight: 180 }} muted playsInline />
            <div className="flex gap-2 mb-3">
              <button onClick={startCam} className="flex-1 rounded-lg bg-[#2B1F27] text-white text-sm py-2" data-testid="scan-camera">Start camera</button>
              <label className="flex-1 rounded-lg border text-sm py-2 text-center cursor-pointer">
                Photo of the barcode
                <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && photo(e.target.files[0])} data-testid="scan-photo-input" />
              </label>
            </div>
            <div className="flex gap-2">
              <input value={code} onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && find(code)}
                placeholder="Type the barcode, SKU or name"
                className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="scan-code-input" />
              <button onClick={() => find(code)} className="rounded-lg border px-4 text-sm" data-testid="scan-find-btn">Find</button>
            </div>
            {hit && (
              <div className="mt-4 border rounded-xl p-3" data-testid="scan-result">
                <p className="font-semibold text-sm mb-1">{hit.label}</p>
                {hit.stock.map((s, i) => (
                  <p key={i} className="text-xs text-muted-foreground">{s.where}{s.size ? ` · ${s.size}` : ''}: {s.qty ?? '—'} in stock</p>
                ))}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => { localStorage.setItem('scan_add_code', hit.code); window.location.href = '/dashboard/orders/new'; }}
                    className="flex-1 rounded-lg bg-[#2B1F27] text-white text-xs py-2" data-testid="scan-add-order">Add to order</button>
                  <button onClick={stockOut} className="flex-1 rounded-lg border text-xs py-2" data-testid="scan-stock-out">Stock out 1</button>
                </div>
              </div>
            )}
          </div>
        </div>, document.body)}
    </>
  );
};
